import { Accordion } from 'QuoteApp/components';
import {
  Disclosures,
  GetDisclosuresResponseType,
  transformDisclosuresResponseWithConfig,
} from 'QuoteApp/services/endpoints/GetDisclosures';
import { useAppContext } from 'QuoteApp/services/providers';
import { LoadingPage } from 'Shared/components';
import { useLazyQuoteApi } from 'Shared/services/Api';
import React, { useCallback, useEffect, useState } from 'react';
import { Asset, ClockIcon } from '@clearcover/atlas';
import { useAuth0 } from 'Auth';

import styles from './styles.module.css';
import { StepItem, StepItemModal } from '../StepItem';
import { addDaysToDate } from 'Shared/utils';

export type POST_BIND_STEP_TYPE = {
  name: string;
  heading: string;
  content?: string | null;
  tag?: {
    type: POST_BIND_STEP_TAG_TYPE;
    text?: string;
  };
  modal?: StepItemModal;
  isDisclosure: boolean;
};

export enum POST_BIND_STEP_TAG_TYPE {
  DUE = 'due',
  STATIC = 'static',
}

type STEP_TYPE = POST_BIND_STEP_TYPE & { loading: boolean };

const POST_BIND_STEPS: POST_BIND_STEP_TYPE[] = [
  {
    name: Disclosures.CustomerDocuments,
    heading: 'Complete Signature Packet',
    tag: {
      type: POST_BIND_STEP_TAG_TYPE.DUE,
    },
    isDisclosure: true,
  },
  {
    name: 'download_mobile_app',
    heading: 'Download the Mobile App',
    content:
      'On the Clearcover award-winning app, your customer can manage their policy, track vehicle maintenance, access policy documents and their ID card.',
    tag: {
      type: POST_BIND_STEP_TAG_TYPE.STATIC,
      text: 'ASAP',
    },
    isDisclosure: false,
  },
  {
    name: Disclosures.VehiclePhotos,
    heading: 'Complete Vehicle Setup',
    tag: {
      type: POST_BIND_STEP_TAG_TYPE.DUE,
    },
    isDisclosure: true,
  },
  {
    name: Disclosures.ProofOfForeignDriversLicense,
    heading: 'Submit Proof of Foreign Driver’s License',
    tag: {
      type: POST_BIND_STEP_TAG_TYPE.DUE,
    },
    isDisclosure: true,
  },
  {
    name: Disclosures.MilitaryDiscount,
    heading: 'Submit Military Discount Verification',
    isDisclosure: true,
  },
  {
    name: Disclosures.GoodStudentDiscount,
    heading: 'Submit Good Student Discount Verification',
    modal: {
      buttonText: 'What types of proof does Clearcover accept?',
      headerText: 'What types of proof can I send for Good Student Discount?',
      bodyContent: (
        <>
          <p>We will accept the following for proof of academic performance:</p>
          <ul>
            <li>School Transcript</li>
            <li>Report Card</li>
            <li>Letter from a school administrator</li>
            <li>Dean's List documentation</li>
            <li>Honor Roll documentation</li>
          </ul>
        </>
      ),
    },
    isDisclosure: true,
  },
  {
    name: 'telematics_enrollment',
    heading: 'Activate Clearcover ClearClaims+',
    content: `To maintain the ClearClaims+ discount, the customer must activate it in the mobile app. Once they download the app, the activation process will begin when they log in.
      <a
        href="https://resources.clearcover.com/hc/en-us/articles/27920745660951/live_preview/01JE95RPWK5B3Z7ATBDGEV2DC9"
        target="_blank"
        rel="noopener noreferrer"
        style="display: block; text-align: left; margin-top: 18px;"
      >Learn More</a>`,
    tag: {
      type: POST_BIND_STEP_TAG_TYPE.DUE,
    },
    isDisclosure: false,
  },
];

export const PostBindSteps = () => {
  const { user } = useAuth0();
  const { details, appId, config } = useAppContext();

  const shouldIncludeCustomerDocumentsStep = () => {
    const isOchoAgent = user?.partner?.name === 'Ocho Insurance Solutions LLC';
    const isDocumentSigningHandledByPartner = isOchoAgent;

    return !isDocumentSigningHandledByPartner;
  };

  const shouldIncludeTelematicsEnrollmentStep = () => {
    if (!config?.telematics?.enabled) return false;
    if (!details.telematics?.enrolled) return false;

    return true;
  };

  const shouldIncludeStep = (step: POST_BIND_STEP_TYPE): boolean => {
    if (step.name === Disclosures.CustomerDocuments) {
      return shouldIncludeCustomerDocumentsStep();
    }

    if (step.name === 'telematics_enrollment') {
      return shouldIncludeTelematicsEnrollmentStep();
    }

    return true;
  };

  const postBindSteps: POST_BIND_STEP_TYPE[] = POST_BIND_STEPS.filter(
    postBindStep =>
      postBindStep.name ===
        details.disclosures.find(
          disclosure => disclosure.attributes.documentName === postBindStep.name
        )?.attributes.documentName || !postBindStep.isDisclosure
  ).filter(shouldIncludeStep);

  const [stepsState, setStepState] = useState<STEP_TYPE[]>([
    ...postBindSteps.map(step => ({ ...step, loading: false })),
  ]);

  // must memoize to avoid infinite render loop in useLazyQuoteApi
  const disclosureTransformer = useCallback(
    res =>
      transformDisclosuresResponseWithConfig(res, {
        // need target "_blank" to not be stripped
        ADD_ATTR: ['target'],
      }),
    []
  );

  const [{ loading: loadingDisclosure }, fetchDisclosure] = useLazyQuoteApi<
    GetDisclosuresResponseType
  >({
    transformResponse: disclosureTransformer,
  });

  const updateStep = useCallback(
    (
      props: {
        loading: boolean;
        content?: string | null;
        tag?: {
          type: POST_BIND_STEP_TAG_TYPE;
          text?: string;
        };
      },
      index: number
    ) =>
      stepsState.map((step, stepIndex) => {
        if (stepIndex === index) {
          step = { ...step, ...props };
        }

        return step;
      }),
    [stepsState]
  );

  const handleFetchDisclosure = useCallback(
    async (disclosure: STEP_TYPE) => {
      if (!disclosure.loading && !loadingDisclosure) {
        const stepIndex = stepsState.findIndex(
          step => step.name === disclosure.name
        );

        setStepState(updateStep({ loading: true }, stepIndex));

        try {
          const disclosureResponse = await fetchDisclosure({
            url: `insurance_applications/${appId}/document_signatures/${disclosure.name}`,
          });

          const { bodyTextSanitized, dueDate } = disclosureResponse.data;

          const stepDataToUpdate: any = {
            loading: false,
            content: bodyTextSanitized.__html,
          };

          if (dueDate && disclosure.tag?.type === POST_BIND_STEP_TAG_TYPE.DUE) {
            stepDataToUpdate.tag = {
              type: disclosure.tag.type,
              text: `Due by ${dueDate}`,
            };
          }

          setStepState(updateStep(stepDataToUpdate, stepIndex));
        } catch (error) {
          console.warn(error);
        }
      }
    },
    [appId, fetchDisclosure, loadingDisclosure, stepsState, updateStep]
  );

  const updateTelematicsEnrollmentStep = (
    enrollmentWindow: number,
    effectiveDate: string
  ) => (stepsState: STEP_TYPE[]) => {
    const dueDate = addDaysToDate(effectiveDate, enrollmentWindow);

    return stepsState.map(step => {
      if (step.name === 'telematics_enrollment') {
        return {
          ...step,
          tag: {
            type: POST_BIND_STEP_TAG_TYPE.DUE,
            text: `Due by ${dueDate}`,
          },
        };
      }

      return step;
    });
  };

  const includeTelematics = shouldIncludeTelematicsEnrollmentStep();

  const disclosureToFetch = stepsState.find(
    step =>
      step.name ===
        details.disclosures.find(
          disclosure => disclosure.attributes.documentName === step.name
        )?.attributes.documentName &&
      step.isDisclosure &&
      !step.content
  );

  useEffect(() => {
    if (disclosureToFetch) handleFetchDisclosure(disclosureToFetch);
  }, [
    config,
    stepsState,
    details.disclosures,
    handleFetchDisclosure,
    disclosureToFetch,
  ]);

  useEffect(() => {
    if (includeTelematics && config && details) {
      setStepState(
        updateTelematicsEnrollmentStep(
          config.telematics.enrollmentWindow,
          details.effectiveDate.value
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeTelematics]);

  const loadingDisclosures = stepsState.some(
    step => step.loading || (step.isDisclosure && !step.content)
  );

  if (loadingDisclosures) {
    return <LoadingPage />;
  }

  return (
    <Accordion className={styles.stepsAccordion}>
      {Object.values(stepsState).map((step, index) => (
        <StepItem
          key={`step-item-${index}`}
          stepNumber={index + 1}
          name={step.name}
          heading={step.heading}
          content={step.content}
          startOpen={index === 0}
          modal={step.modal}
          tag={
            step.tag?.text ? (
              <>
                <Asset
                  className={styles.tagIcon}
                  icon={ClockIcon}
                  height={18}
                  width={16}
                  description="clok-icon"
                />
                <b>{step.tag?.text}</b>
              </>
            ) : null
          }
        />
      ))}
    </Accordion>
  );
};
